import React, { useState } from 'react';
import { Star, MessageSquare, Pin, Languages, Star as StarIcon } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';

interface ReviewKeywordSentiment {
  text: string;
  sentiment: 'positive' | 'negative' | 'neutral';
}

interface Review {
  id: number;
  patientName: string;
  patientImage?: string;
  rating: number;
  date: string;
  comment: string;
  treatmentType?: string;
  doctorReply?: string;
  language?: string;
  translation?: string;
  isPinned?: boolean;
  keywords?: ReviewKeywordSentiment[];
}

interface ReviewWithReplyProps {
  review: Review;
  onReply: (id: number, reply: string) => void;
  onPin: (id: number) => void;
  onTranslate: (id: number) => void;
  pinned: boolean;
}

const ReviewWithReply: React.FC<ReviewWithReplyProps> = ({ 
  review, 
  onReply, 
  onPin,
  onTranslate,
  pinned
}) => {
  const [replying, setReplying] = useState(false);
  const [replyText, setReplyText] = useState('');
  const { toast } = useToast();
  const [showTranslation, setShowTranslation] = useState(false);

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };
  
  const renderStars = (rating: number) => {
    return Array(5).fill(0).map((_, i) => (
      <Star 
        key={i} 
        className={`w-4 h-4 ${i < rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} 
      />
    ));
  };

  const handleSubmitReply = () => {
    if (replyText.trim().length === 0) {
      toast({
        description: "Reply cannot be empty",
        variant: "destructive"
      });
      return;
    }

    if (replyText.length > 300) {
      toast({
        description: "Reply must be 300 characters or less",
        variant: "destructive"
      });
      return;
    }
    
    onReply(review.id, replyText);
    setReplying(false);
    setReplyText('');
    
    toast({
      description: "Your reply has been posted",
    });
  };

  const toggleTranslation = () => {
    if (!showTranslation && !review.translation) {
      onTranslate(review.id);
    }
    setShowTranslation(!showTranslation);
  };

  const highlightSentiment = (text: string) => {
    if (!review.keywords || review.keywords.length === 0) {
      return <span>{text}</span>;
    }

    let result = text;
    const elements: JSX.Element[] = [];
    let lastIndex = 0;

    const sortedKeywords = [...review.keywords].sort((a, b) => {
      return text.indexOf(a.text) - text.indexOf(b.text);
    });

    sortedKeywords.forEach((keyword) => {
      const index = result.indexOf(keyword.text, lastIndex);
      if (index !== -1) {
        if (index > lastIndex) {
          elements.push(<span key={`text-${lastIndex}`}>{result.substring(lastIndex, index)}</span>);
        }

        elements.push(
          <Badge 
            key={`keyword-${index}`}
            variant="outline" 
            className={`px-1 py-0 mx-1 text-xs ${
              keyword.sentiment === 'positive' 
                ? 'bg-green-50 text-green-700 border-green-200' 
                : keyword.sentiment === 'negative'
                ? 'bg-red-50 text-red-700 border-red-200'
                : ''
            }`}
          >
            {keyword.text}
          </Badge>
        );

        lastIndex = index + keyword.text.length;
      }
    });

    if (lastIndex < result.length) {
      elements.push(<span key={`text-${lastIndex}`}>{result.substring(lastIndex)}</span>);
    }

    return <>{elements}</>;
  };

  return (
    <Card className={`overflow-hidden ${pinned ? 'border-primary/30 bg-primary/5' : ''}`}>
      <CardContent className="p-4 relative">
        {pinned && (
          <Badge className="absolute top-2 right-2 bg-primary text-white">
            Featured
          </Badge>
        )}
        
        <div className="flex items-start gap-4">
          <Avatar className="w-10 h-10">
            {review.patientImage ? (
              <AvatarImage src={review.patientImage} alt={review.patientName} />
            ) : (
              <AvatarFallback>{getInitials(review.patientName)}</AvatarFallback>
            )}
          </Avatar>
          
          <div className="flex-1">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-medium">{review.patientName}</h3>
                <div className="flex mt-1">
                  {renderStars(review.rating)}
                </div>
              </div>
              <span className="text-sm text-muted-foreground">
                {format(new Date(review.date), 'MMM d, yyyy')}
              </span>
            </div>
            
            <p className="mt-2 text-sm text-gray-600">
              {highlightSentiment(review.comment)}
            </p>

            {review.language && review.language !== 'en' && (
              <div className="mt-2">
                <Button 
                  variant="ghost" 
                  size="sm"
                  className="h-7 px-2 text-xs flex items-center gap-1"
                  onClick={toggleTranslation}
                >
                  <Languages className="h-3 w-3" />
                  {showTranslation ? "Show Original" : "Translate"}
                </Button>
                
                {showTranslation && review.translation && (
                  <p className="mt-1 text-sm text-gray-600 italic border-l-2 pl-2 border-gray-300">
                    {review.translation}
                  </p>
                )}
              </div>
            )}
            
            {review.treatmentType && (
              <div className="mt-2">
                <Badge variant="secondary" className="text-xs">
                  {review.treatmentType}
                </Badge>
              </div>
            )}
            
            <div className="mt-3 flex gap-2">
              {!review.doctorReply && !replying && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="h-8"
                  onClick={() => setReplying(true)}
                >
                  <MessageSquare className="h-4 w-4 mr-1" />
                  Reply
                </Button>
              )}
              
              <Button 
                variant={pinned ? "secondary" : "outline"}
                size="sm" 
                className="h-8"
                onClick={() => onPin(review.id)}
              >
                <Pin className={`h-4 w-4 mr-1 ${pinned ? 'text-primary fill-primary' : ''}`} />
                {pinned ? "Unpin" : "Pin"}
              </Button>
            </div>

            {replying && (
              <div className="mt-4 space-y-3">
                <Textarea 
                  placeholder="Write your reply (max 300 characters)..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  className="resize-none"
                  maxLength={300}
                />
                <div className="flex justify-between items-center">
                  <span className="text-xs text-muted-foreground">
                    {replyText.length}/300 characters
                  </span>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setReplying(false);
                        setReplyText('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button 
                      size="sm"
                      onClick={handleSubmitReply}
                    >
                      Post Reply
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            {review.doctorReply && (
              <div className="mt-3 bg-muted/50 p-3 rounded-md">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className="text-xs">Doctor's Reply</Badge>
                  {/* Can add reply date here if available */}
                </div>
                <p className="text-sm">{review.doctorReply}</p>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ReviewWithReply;
